import { useMemo, useState } from 'react'
import {
  Shield, Settings, CheckCircle2, Clock, XCircle, Lock, Loader2,
  Search, SlidersHorizontal, ListFilter, Info, ShieldCheck
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useToolPermissions, useSetToolPermission, useSetGlobalPermissionMode } from '@/hooks/useApi'
import { cn } from '@/lib/utils'

type Permission = 'always_allowed' | 'needs_approval' | 'blocked'

const TIER_COLORS: Record<number, string> = {
  1: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
  2: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/20',
  3: 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/20',
}

const TIER_LABELS: Record<number, string> = { 1: 'Read', 2: 'Write', 3: 'Delete' }

const GLOBAL_MODES = [
  { value: 'custom', label: 'Custom', desc: 'Per-tool overrides apply. Tier 2+ default to needs approval.' },
  { value: 'allow_all', label: 'Allow All', desc: 'Tier 2 tools may run without individual approval. Tier 3 remains human-gated.' },
  { value: 'require_all', label: 'Require All', desc: 'Every tool requires approval regardless of its individual setting.' },
]

const PERMS: Array<{
  value: Permission
  label: string
  icon: typeof CheckCircle2
  active: string
  hover: string
}> = [
  {
    value: 'always_allowed',
    label: 'Allowed',
    icon: CheckCircle2,
    active: 'bg-emerald-500 border-emerald-400 text-white shadow-[0_8px_20px_rgba(16,185,129,.22),inset_0_1px_0_rgba(255,255,255,.28)]',
    hover: 'hover:border-emerald-400/50 hover:text-emerald-500',
  },
  {
    value: 'needs_approval',
    label: 'Approval',
    icon: Clock,
    active: 'bg-amber-500 border-amber-400 text-white shadow-[0_8px_20px_rgba(245,158,11,.22),inset_0_1px_0_rgba(255,255,255,.28)]',
    hover: 'hover:border-amber-400/50 hover:text-amber-500',
  },
  {
    value: 'blocked',
    label: 'Blocked',
    icon: XCircle,
    active: 'bg-rose-500 border-rose-400 text-white shadow-[0_8px_20px_rgba(244,63,94,.20),inset_0_1px_0_rgba(255,255,255,.28)]',
    hover: 'hover:border-rose-400/50 hover:text-rose-500',
  },
]

export function ToolPermissionsPanel({ embedded = false }: { embedded?: boolean }) {
  const { data, isLoading } = useToolPermissions()
  const setPermission = useSetToolPermission()
  const setGlobalMode = useSetGlobalPermissionMode()
  const [filter, setFilter] = useState('')
  const [tierFilter, setTierFilter] = useState<number | null>(null)
  const [pendingTool, setPendingTool] = useState<string | null>(null)

  const tools = data?.tools || []
  const globalMode = data?.global_mode || 'custom'

  const filtered = useMemo(() => tools.filter(t =>
    (tierFilter === null || t.tier === tierFilter) &&
    (!filter || t.name.toLowerCase().includes(filter.toLowerCase()) || (t.description || '').toLowerCase().includes(filter.toLowerCase()))
  ), [tools, tierFilter, filter])

  const stats = [
    { label: 'Total', value: tools.length, tone: 'blue', icon: ListFilter },
    { label: 'Auto-Allowed', value: tools.filter(t => t.permission === 'always_allowed').length, tone: 'green', icon: CheckCircle2 },
    { label: 'Needs Approval', value: tools.filter(t => t.permission === 'needs_approval').length, tone: 'amber', icon: Clock },
    { label: 'Blocked', value: tools.filter(t => t.permission === 'blocked').length, tone: 'red', icon: XCircle },
  ] as const

  function handlePerm(toolName: string, permission: Permission) {
    setPendingTool(toolName)
    setPermission.mutate(
      { toolName, permission },
      { onSettled: () => setPendingTool(null) }
    )
  }

  return (
    <div className={cn('space-y-4', !embedded && 'max-w-6xl mx-auto')}>
      <section className="ops-governance-surface rounded-2xl p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="ops-icon-tile ops-icon-tile-group"><Settings className="h-5 w-5" /></span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[17px] font-semibold text-text-primary">Global Permission Mode</h3>
                {(isLoading || setGlobalMode.isPending) && <Loader2 className="h-4 w-4 animate-spin text-text-muted" />}
              </div>
              <p className="text-xs text-text-muted mt-1">Choose the workspace-wide baseline. Individual tool controls still remain visible below.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-text-muted">
            <Info className="h-3.5 w-3.5" />
            Applies to all tools
          </div>
        </div>

        <div className="ops-permission-mode-grid">
          {GLOBAL_MODES.map(mode => {
            const active = globalMode === mode.value
            return (
              <button
                key={mode.value}
                onClick={() => setGlobalMode.mutate(mode.value)}
                disabled={setGlobalMode.isPending}
                className={cn('ops-permission-mode-button', active && 'ops-permission-mode-button-active')}
              >
                <span className="font-semibold">{mode.label}</span>
              </button>
            )
          })}
        </div>

        <div className="mt-3 text-xs text-text-muted">
          {GLOBAL_MODES.find(mode => mode.value === globalMode)?.desc}
        </div>
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map(stat => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className={cn('ops-permission-stat', 'ops-permission-stat-' + stat.tone)}>
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4" />
                <span className="text-[10px] uppercase tracking-[.14em] opacity-60">Live</span>
              </div>
              <div className="text-3xl font-semibold mt-3 tabular-nums">{stat.value}</div>
              <div className="text-xs mt-1 opacity-75">{stat.label}</div>
            </div>
          )
        })}
      </div>

      <section className="ops-governance-surface rounded-2xl p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="ops-icon-tile ops-icon-tile-group"><Shield className="h-5 w-5" /></span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[17px] font-semibold text-text-primary">Tool Permissions</h3>
                <Badge variant="muted">{filtered.length}</Badge>
                {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted" />}
              </div>
              <p className="text-xs text-text-muted mt-1">Set whether each tool is allowed, approval-gated, or blocked.</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[10px] text-text-muted">
            <ShieldCheck className="h-3.5 w-3.5 text-accent" /> Tier 3 cannot be auto-allowed
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-3">
          <label className="ops-tool-search">
            <Search className="h-4 w-4" />
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Search tools..."
              aria-label="Search tools"
            />
          </label>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-text-muted hidden sm:block" />
            <div className="ops-tier-chip-row">
              {([null, 1, 2, 3] as Array<number | null>).map(tier => (
                <button
                  key={tier ?? 'all'}
                  onClick={() => setTierFilter(tier)}
                  className={cn('ops-tier-chip', tierFilter === tier && 'ops-tier-chip-active')}
                >
                  {tier === null ? 'All' : 'T' + tier}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ops-tool-permission-head hidden md:grid">
          <div>Tool</div>
          <div className="text-center">Allowed</div>
          <div className="text-center">Approval</div>
          <div className="text-center">Blocked</div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading tool permissions…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-text-muted">No tools match this filter.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(tool => {
              const isT3 = tool.tier >= 3
              const isPending = pendingTool === tool.name
              return (
                <div key={tool.name} className={cn('ops-tool-permission-row', isPending && 'opacity-70')}>
                  <div className="flex items-start gap-3 min-w-0">
                    <span
                      className={cn('text-[10px] px-2 py-1 rounded-lg border font-mono font-bold shrink-0 mt-0.5', TIER_COLORS[tool.tier])}
                      title={TIER_LABELS[tool.tier]}
                    >
                      {'T' + tool.tier}
                    </span>
                    <div className="min-w-0">
                      <div className="font-mono text-[12px] sm:text-[13px] text-text-primary break-all">{tool.name}</div>
                      {tool.description && <div className="text-[10px] text-text-muted mt-1 line-clamp-2">{tool.description}</div>}
                    </div>
                  </div>

                  <div className="ops-tool-permission-actions">
                    {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted mr-1" />}
                    {PERMS.map(permission => {
                      const cannotAllow = isT3 && permission.value === 'always_allowed'
                      const active = tool.permission === permission.value
                      const Icon = permission.icon
                      return (
                        <button
                          key={permission.value}
                          onClick={() => !cannotAllow && handlePerm(tool.name, permission.value)}
                          disabled={cannotAllow || isPending}
                          title={cannotAllow ? 'Tier 3 tools cannot be always allowed' : permission.label}
                          className={cn(
                            'ops-tool-permission-action',
                            cannotAllow
                              ? 'opacity-25 cursor-not-allowed'
                              : active
                                ? permission.active
                                : cn('bg-surface-elevated/45 border-border text-text-muted', permission.hover)
                          )}
                        >
                          {cannotAllow ? <Lock className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                          <span className="hidden sm:inline">{permission.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
